import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { useLazyGetEnterpriseThermostatsQuery } from "store/api/enterpriseApi"
import { useAppDispatch } from "store/hooks"
import { setSelectedDevicesForPerformanceTest } from "store/tempDataSlise"
import { ApiTypes, UITypes } from "types"
import PerformanceTestStatus from "types/enums/PerformanceTestStatus"
import { ReactComponent as IconPower } from "assets/svg/icon-power.svg"
import { ReactComponent as IconStatsReport } from "assets/svg/icon-stats-report.svg"
import { ReactComponent as IconPerformanceTest } from "assets/svg/menu-performance.svg"

import {
  convertEnterpriseMonitoringToTableRows,
  convertEnterpriseThermostatToMonitoringDevice,
} from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import DeviceRebootModal from "modals/DeviceRebootModal/DeviceRebootModal"
import { PerformanceTestActionsModal } from "modals/PerformanceTestActionsModal"
import { StatsReportModal } from "modals/StatsReportModal"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import EditColumnsButton from "components/EditColumnsButton"
import TableWrapper from "components/table/TableWrapper"

import { enterpriseMonitoringTableHeaderCells } from "./enterpriseMonitoringTableHeaderCells"

const TABLE_NAME = "admin_devices_list"

export const EnterpriseMonitoringPage = () => {
  const dispatch = useAppDispatch()
  const { urlQueryParams } = useUrlQueryParams()
  const [triggerGetMonitoringData, { isFetching, data }] = useLazyGetEnterpriseThermostatsQuery({
    pollingInterval: 30000,
    skipPollingIfUnfocused: true,
  })
  const [triggerGetTableColumns, { data: columnsData, isFetching: isColumnsFetching }] = useLazyGetTableColumnsQuery()
  const [showPerformanceTestActionsModal, setShowPerformanceTestActionsModal] = useState(false)
  const [showRebootModalDevice, setShowRebootModalDevice] = useState<ApiTypes.Model.Device | undefined>()
  const [selectedDeviceForStatsReport, setSelectedDeviceForStatsReport] = useState<ApiTypes.Model.Device | undefined>()
  const devices = data?.data ?? []

  const tableHeaderCells = useMemo(
    (): UITypes.TableHeaderCell[] =>
      columnsData?.data?.config
        ? reorderTableHeaderCells(enterpriseMonitoringTableHeaderCells, columnsData.data.config)
        : enterpriseMonitoringTableHeaderCells,
    [columnsData],
  )

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await triggerGetMonitoringData({ phisical_status: "installed", ...params })
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const fetchData = () => {
    sendRequest(urlQueryParams)
  }

  useEffect(() => {
    fetchData()
  }, [urlQueryParams])

  useEffect(() => {
    triggerGetTableColumns({ list_name: TABLE_NAME })
  }, [triggerGetTableColumns])

  const handlePerformanceTestSuccess = () => {
    setTimeout(fetchData, 2000)
    setTimeout(fetchData, 5000)
  }

  const handleShowActionsModal = (id: number) => {
    const device = devices.find(item => item.id === id)

    dispatch(
      setSelectedDevicesForPerformanceTest({
        isImmediate: true,
        devices: [
          {
            id,
            email: device?.email,
            sn: device?.sn,
            system_type: device?.system_type,
            status: device?.perftest?.status,
          },
        ] as ApiTypes.Model.PerformanceTestDevice[],
      }),
    )
    setShowPerformanceTestActionsModal(true)
  }

  const handleShowStatsReportModal = (id: number) => {
    const device = devices.find(item => item.id === id)
    setSelectedDeviceForStatsReport(device ? convertEnterpriseThermostatToMonitoringDevice(device) : undefined)
  }

  const handleShowRebootModal = (id: number) => {
    const device = devices.find(item => item.id === id)
    setShowRebootModalDevice(device ? convertEnterpriseThermostatToMonitoringDevice(device) : undefined)
  }

  const getRowActions = (rowData: UITypes.TableBodyRow) => {
    const isOffline = rowData.rowSettings.item.work_status === "offline"
    const isRunning = rowData.rowSettings.item.perftest?.status === PerformanceTestStatus.RUNNING

    return {
      actionButtons: [
        {
          icon: <IconPower />,
          tooltipPosition: "top" as const,
          tooltip: (
            <>
              Reboot device
              {isOffline && (
                <>
                  <br />
                  (device is offline)
                </>
              )}
            </>
          ),
          disabled: isOffline,
          handler: handleShowRebootModal,
        },
        {
          icon: <IconPerformanceTest />,
          tooltipPosition: "top-start" as const,
          tooltip: (
            <>
              Start performance test
              {isOffline && (
                <>
                  <br />
                  (device is offline)
                </>
              )}
              {isRunning && !isOffline && (
                <>
                  <br />
                  (performance test is running)
                </>
              )}
            </>
          ),
          disabled: isOffline || isRunning,
          handler: handleShowActionsModal,
        },
        {
          icon: <IconStatsReport />,
          tooltipPosition: "top-start" as const,
          tooltip: "Monitoring report",
          handler: handleShowStatsReportModal,
        },
      ],
    }
  }

  return (
    <>
      <ContentWrapper
        title="Monitoring Page"
        renderHeaderAccessoryView={() => (
          <EditColumnsButton tableName={TABLE_NAME} tableHeaderCells={tableHeaderCells} />
        )}
      >
        <TableWrapper
          headerCells={tableHeaderCells}
          columnsLoaded={!isColumnsFetching}
          data={convertEnterpriseMonitoringToTableRows(devices)}
          getRowActions={getRowActions}
          isLoading={isFetching}
        />
        <Pagination meta={data?.meta} />
      </ContentWrapper>
      {showRebootModalDevice && (
        <DeviceRebootModal device={showRebootModalDevice} onClose={() => setShowRebootModalDevice(undefined)} />
      )}
      {showPerformanceTestActionsModal && (
        <PerformanceTestActionsModal
          onCancel={() => setShowPerformanceTestActionsModal(false)}
          onSuccess={handlePerformanceTestSuccess}
        />
      )}
      {selectedDeviceForStatsReport && (
        <StatsReportModal
          onCancel={() => setSelectedDeviceForStatsReport(undefined)}
          device={selectedDeviceForStatsReport}
        />
      )}
    </>
  )
}

export default EnterpriseMonitoringPage
